import { useEffect, useMemo, useRef, useState } from 'react';
import { ContextImage, ImageData } from '@v2/context/Image/context/interfaces';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { removeMessageById, selectMessagesByKey } from '@reducers/v2/messagesBus/messagesBus.slice';
import { Message, ImageDocumentMessageType } from '@reducers/v2/messagesBus/types';
import { UploadedImage } from '@v2/context/Image/interfaces';
import { useHandleImageNavigation } from '@v2/pages/common/hooks/useHandleImageNavigation';
import { selectFavoriteImageByScope, selectScopeHasAnyImage } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { PixelRatio } from 'react-native';
import { generateImageURL } from '@reducers/blobStorage';
import { CONTEXT_TO_NAME, KEYS_TAGS } from '@components/imageHandling/common';
import { GestureResponderEvent } from 'react-native-modal';
import { ImageUpload, isWeb } from '@helpers';
import { backendCall } from '@reducers/utils';
import { ImageDocumentStoreItem, ImageDocumentStoreItemWithAngle } from '@v2/components/imageHandling/types/types';
import { cropImageDocument } from '@reducers/v2/imageHandlingV2/thunks/cropImageDocument';
import { ContextInternalProps } from '@v2/context/Image/context';
import { buildUniqueParamsForImageUrl, calculateCropDimensions } from '@reducers/v2/imageHandlingV2/thunks';
import { buildImageDataFromDocument } from '@v2/components/imageHandling/helpers';

const imageUrlCache = new Map<string, string>();
const imageOriginUrlCache = new Map<string, string>();
const EMPTY_MESSAGES: Message[] = [];

const getImageCacheKey = (imageHash: string | undefined | null, imageData: ImageData | undefined, width: number, height: number) => {
  if (!imageHash || !imageData) {
    return null;
  }

  const { pageNumber, extensionDerivative, isCropped } = imageData;
  return [imageHash, pageNumber ?? 1, extensionDerivative ?? 'none', isCropped ? 'cropped' : 'full', width, height].join('_');
};

interface UseManageContextImageParams
  extends Pick<ContextInternalProps, 'image' | 'onPress' | 'showImageControl' | 'attachMode' | 'imageDocumentReferences'> {}

export const useManageContextImage = ({ image, onPress, showImageControl, attachMode, imageDocumentReferences }: UseManageContextImageParams) => {
  const rotateTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dispatch = useAppDispatch();

  const scopeId = KEYS_TAGS[CONTEXT_TO_NAME[image.contextCode] as keyof typeof KEYS_TAGS] as number | undefined;
  const scopeReferenceId = image.contextId;

  const favoriteImageSelector = useMemo(() => selectFavoriteImageByScope(scopeId, scopeReferenceId), [scopeId, scopeReferenceId]);
  const favoriteImage = useSelector(favoriteImageSelector);

  const scopeHasAnyImageSelector = useMemo(() => selectScopeHasAnyImage(scopeId, scopeReferenceId), [scopeId, scopeReferenceId]);
  const scopeHasAnyImage = useSelector(scopeHasAnyImageSelector);

  const useBootstrap = !favoriteImage && !scopeHasAnyImage;

  const effectiveDocumentId = favoriteImage?.documentId ?? (useBootstrap ? image.documentId : undefined);

  const imageFromStore = useSelector((state: RootState) =>
    effectiveDocumentId ? (state.imageHandlingV2.imagesDocumentsDict as Record<number, ImageDocumentStoreItem>)[effectiveDocumentId] : undefined
  );

  const effectiveSource = imageFromStore ?? favoriteImage;
  const effectiveImageHash = effectiveSource?.contentHash ?? (useBootstrap ? image.imageHash : undefined);
  const effectiveImageData = useMemo<ImageData | undefined>(
    () => {
      if (effectiveSource) return buildImageDataFromDocument(effectiveSource, scopeId);
      return useBootstrap ? image.imageData : undefined;
    },
    [effectiveSource, image.imageData, scopeId, useBootstrap]
  );


  const imageCacheKey = getImageCacheKey(effectiveImageHash, effectiveImageData, image.width, image.height);

  const documentMessages = useSelector((state: RootState) =>
    effectiveDocumentId ? selectMessagesByKey(state, effectiveDocumentId) : EMPTY_MESSAGES
  );

  const [imageLink, setImageLink] = useState<string | null>(() => (imageCacheKey ? imageUrlCache.get(imageCacheKey) ?? null : null));
  const [originUrlImageLink, setOriginUrlImageLink] = useState<string | null>(() =>
    imageCacheKey ? imageOriginUrlCache.get(imageCacheKey) ?? null : null
  );
  const [localRotation, setLocalRotation] = useState<number>(effectiveImageData?.isCropped ? 0 : effectiveImageData?.rotationAngle || 0);
  const [localCropRotation, setLocalCropRotation] = useState<number>(effectiveImageData?.rotationAngleCrop || 0);
  const [uploadedImage, setUploadedImage] = useState<UploadedImage | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCrop, setShowCrop] = useState(false);

  const imageSize = useRef<string | null>(null);

  const sawEffectiveDocRef = useRef(false);
  useEffect(() => {
    sawEffectiveDocRef.current = false;
  }, [effectiveDocumentId]);
  useEffect(() => {
    if (imageFromStore) sawEffectiveDocRef.current = true;
  }, [imageFromStore]);
  const imageDeleted = sawEffectiveDocRef.current && !imageFromStore && effectiveDocumentId != null;

  useEffect(() => {
    if (!imageDeleted) return;
    setImageLink(null);
    setOriginUrlImageLink(null);
    if (imageCacheKey) {
      imageUrlCache.delete(imageCacheKey);
      imageOriginUrlCache.delete(imageCacheKey);
    }
  }, [imageDeleted, imageCacheKey]);

  useEffect(() => {
    if (uploadedImage) {
      setLocalRotation(0);
      setLocalCropRotation(0);
      return;
    }
    setLocalRotation(effectiveImageData?.isCropped ? 0 : effectiveImageData?.rotationAngle || 0);
    setLocalCropRotation(effectiveImageData?.rotationAngleCrop || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDocumentId, uploadedImage]);

  const { borderRadius = 10 } = image;

  const { handleImageNavigation } = useHandleImageNavigation();

  const handleCropEvent = async (payload: any) => {
    if (!effectiveImageHash || !effectiveImageData) return;
    const additionalParams = payload.cropSettings ? buildUniqueParamsForImageUrl(payload.cropSettings) : null;
    const newLinkAfterCrop = await dispatch(
      generateImageURL({
        size: imageSize.current || '_1x',
        cropped: true,
        item: { imageHash: effectiveImageHash, extensionDerivative: effectiveImageData.extensionDerivative },
        page: effectiveImageData.pageNumber,
        ...(additionalParams ? { additionalParams } : {}),
      })
    );

    setImageLink(newLinkAfterCrop);
    if (imageCacheKey) {
      imageUrlCache.set(imageCacheKey, newLinkAfterCrop);
    }
  };

  useEffect(() => {
    const relevantMessages = documentMessages.filter((message: Message) => {
      if (![ImageDocumentMessageType.IMAGE_ROTATED, ImageDocumentMessageType.IMAGE_CROPPED].includes(message.messageType)) {
        return false;
      }
      const payload = message.payload;
      const pageNumber = payload.pageNumber || payload.cropSettings?.pageNumber || 1;
      return pageNumber === effectiveImageData?.pageNumber;
    });

    (async () => {
      for (let message of relevantMessages) {
        const now = Date.now();

        if (message.ttl && now > message.ttl) {
          if (effectiveDocumentId != null) {
            dispatch(removeMessageById({ key: effectiveDocumentId, messageId: message.id }));
          }
          continue;
        }

        const payload = message.payload;

        switch (message.messageType) {
          case ImageDocumentMessageType.IMAGE_ROTATED:
            if (payload.isCroppedVersion) {
              handleCropEvent(payload);
            } else {
              setLocalRotation(payload.rotationAngle);
            }
            break;
          case ImageDocumentMessageType.IMAGE_CROPPED:
            handleCropEvent(payload);
            break;
        }
      }
    })();
  }, [documentMessages, effectiveDocumentId, effectiveImageData?.pageNumber, effectiveImageData?.rotationAngleCrop, dispatch]);

  useEffect(() => {
    if (!effectiveImageHash || !effectiveImageData) {
      setImageLink(null);
      setOriginUrlImageLink(null);
      return;
    }

    if (imageCacheKey) {
      if (imageUrlCache.has(imageCacheKey)) {
        setImageLink(imageUrlCache.get(imageCacheKey) ?? null);
      }
      if (imageOriginUrlCache.has(imageCacheKey)) {
        setOriginUrlImageLink(imageOriginUrlCache.get(imageCacheKey) ?? null);
      }
      if (imageUrlCache.has(imageCacheKey) && imageOriginUrlCache.has(imageCacheKey)) {
        return;
      }
    }

    setImageLink(null);
    setOriginUrlImageLink(null);

    let isMounted = true;

    (async () => {
      const max = Math.max(image.width, image.height) * PixelRatio.get();
      const size = (() => {
        if (max <= 504) {
          return '_1x';
        }
        if (max > 504 && max <= 840) {
          return '_2x';
        }

        if (max > 840 && max <= 1680) {
          return '_3x';
        }

        if (max > 1680) {
          return '_large';
        }
        return '_1x';
      })();

      imageSize.current = size;

      try {
        const generatedLink = await dispatch(
          generateImageURL({
            size,
            cropped: effectiveImageData.isCropped,
            item: { imageHash: effectiveImageHash, extensionDerivative: effectiveImageData.extensionDerivative },
            page: effectiveImageData.pageNumber,
          })
        );

        const originLink = await dispatch(
          generateImageURL({
            size: '_large',
            cropped: false,
            item: { imageHash: effectiveImageHash, extensionDerivative: effectiveImageData.extensionDerivative },
            page: effectiveImageData.pageNumber,
          })
        );

        if (!isMounted) {
          return;
        }

        if (imageCacheKey) {
          imageUrlCache.set(imageCacheKey, generatedLink);
          imageOriginUrlCache.set(imageCacheKey, originLink);
        }

        setImageLink(generatedLink);
        setOriginUrlImageLink(originLink);
      } catch (error) {}
    })();

    return () => {
      isMounted = false;
    };
  }, [generateImageURL, imageCacheKey, effectiveImageHash]);

  const possibleAddImage = (image.contextId && !effectiveImageHash && !uploadedImage) || imageDeleted;

  const imageHandlingContext = { documentId: effectiveDocumentId, [image.contextCode]: image.contextId };

  const imageContext = {
    scopeId,
    scopeReferenceId,
  };

  const pressImage = (event: GestureResponderEvent) => {
    if (attachMode && onPress) {
      onPress(event);
    } else if (possibleAddImage && !attachMode) {
      ImageUpload({
        uploadStart: () => setLoading(true),
        uploadEnd: (img: UploadedImage) => {
          setLoading(false);
          setUploadedImage(img);
        },
        imageDocumentReferences,
        tags: [
          {
            ...imageContext,
            isFavorite: false,
          },
        ],
      });
    } else {
      if (onPress) {
        onPress(event);
      } else {
        handleImageNavigation(imageHandlingContext);
      }
    }
  };

  const pressRotateImage = async () => {
    if (rotateTimeout.current) {
      clearTimeout(rotateTimeout.current);
    }

    let angle = localRotation + 90;
    if (angle == 360) {
      angle = 0;
    }

    let cropAngle = localCropRotation + 90;
    if (cropAngle == 360) {
      cropAngle = 0;
    }

    setLocalRotation(angle);
    setLocalCropRotation(cropAngle);

    const isCropped = effectiveImageData?.isCropped;

    rotateTimeout.current = setTimeout(async () => {
      if (effectiveDocumentId == null) return;
      const backendData = {
        documentId: effectiveDocumentId,
        rotationAngle: isCropped ? cropAngle : angle,
        isCroppedVersion: isCropped,
        pageNumber: 1,
      };

      const call = {
        method: (api: any) => api.imageHandling.rotate,
        params: backendData,
        onSuccess: () => {},
        onError: (error: Error) => {
          console.error(`Failed to rotate image with documentId ${effectiveDocumentId}, page 1`, error);
        },
      };

      await dispatch(backendCall(call));
    }, 1000);
  };

  const triggerCrop = () => {
    setShowCrop(!showCrop);
  };

  const saveCroppedImage = async (image: ImageDocumentStoreItemWithAngle) => {
    const { angle, base64 } = image;

    if (base64) {
      setImageLink(base64);
      if (imageCacheKey) {
        imageUrlCache.set(imageCacheKey, base64);
      }
    }
    setLocalRotation(0);
    setLocalCropRotation(angle);

    setShowCrop(false);
    dispatch(cropImageDocument({ imageDocument: image }));
  };

  const isOppositeRotation = !possibleAddImage && [90, 270].includes(localRotation);
  const renderWidth = isOppositeRotation ? image.height : image.width;
  const renderHeight = isOppositeRotation ? image.width : image.height;

  const showControls = !loading && !possibleAddImage && isWeb && showImageControl;

  const getTransformStyle = () => {
    if (possibleAddImage) {
      return {};
    }
    if (isWeb) {
      return { transform: `rotate(${localRotation}deg)` };
    } else {
      return { transform: [{ rotate: `${localRotation}deg` }] };
    }
  };

  return {
    showCrop,
    imageLink,
    originUrlImageLink,
    localRotation,
    saveCroppedImage,
    triggerCrop,
    showControls,
    handleImageNavigation,
    imageHandlingContext,
    pressRotateImage,
    pressImage,
    renderWidth,
    renderHeight,
    borderRadius,
    getTransformStyle,
    loading,
    possibleAddImage,
    uploadedImage,
  };
};
